import { createI18n } from 'vue-i18n';
import { mergeAppKitMessages } from '@lumi/app-kit';
import de from './de';
import en from './en';
import fr from './fr';
import es from './es';
import nl from './nl';
import pl from './pl';
import sv from './sv';
import it from './it';
import pt from './pt';

export const SUPPORTED_LOCALES = ['de', 'en', 'fr', 'es', 'nl', 'pl', 'sv', 'it', 'pt'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

// Native names, so someone who cannot read the current locale can still find
// their own in the picker.
export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  es: 'Español',
  nl: 'Nederlands',
  pl: 'Polski',
  sv: 'Svenska',
  it: 'Italiano',
  pt: 'Português',
};

function isSupported(code: string | null | undefined): code is SupportedLocale {
  return !!code && (SUPPORTED_LOCALES as readonly string[]).includes(code);
}

// account.{domain} is its OWN origin: it cannot read the `kb_locale` a tenant
// host stored, so a first visit falls back to the browser's language before the
// suite default. Once the visitor picks a language it is remembered here.
export function initialLocale(): SupportedLocale {
  try {
    const saved = localStorage.getItem('kb_locale');
    if (isSupported(saved)) return saved;
  } catch {
    // storage blocked (private mode / cookie policy) — fall through
  }
  const preferred = typeof navigator !== 'undefined' ? navigator.language || '' : '';
  const base = preferred.toLowerCase().split('-')[0];
  return isSupported(base) ? base : 'de';
}

export const i18n = createI18n({
  legacy: false,
  locale: initialLocale(),
  fallbackLocale: 'de',
  messages: mergeAppKitMessages({ de, en, fr, es, nl, pl, sv, it, pt }),
});

export function setLocale(code: string) {
  if (!isSupported(code)) return;
  (i18n.global.locale as any).value = code;
  try {
    localStorage.setItem('kb_locale', code);
  } catch {
    // storage blocked — the choice still applies for this visit
  }
}
